/**
 * Request Queue Manager
 * Manages API request queuing to prevent rate limiting and excessive concurrent requests
 */
class RequestQueueManager {
  constructor() {
    this.queue = [];
    this.processing = false;
    this.concurrentRequests = 0;
    this.maxConcurrentRequests = 6; // Increase concurrent requests for better throughput
    this.requestDelay = 10; // Reduced delay between requests (ms)
    this.rateLimitedUntil = null; // Timestamp when rate limiting expires
    this.lastRequestTime = null;
    
    // Track request patterns to detect potential rate limiting
    this.requestHistory = [];
    this.maxHistoryLength = 50;
  }

  /**
   * Add a request to the queue
   * @param {Function} requestFn - Function that returns a promise for the API request
   * @param {Object} options - Request options
   * @returns {Promise} Promise that resolves with the API response
   */
  async enqueue(requestFn, options = {}) {
    return new Promise((resolve, reject) => {
      const requestItem = {
        requestFn,
        options: {
          priority: options.priority || 'normal', // 'high', 'normal', 'low'
          timeout: options.timeout || 10000,
          retries: options.retries || 1,
          ...options
        },
        resolve,
        reject,
        timestamp: Date.now(),
        retryCount: 0
      };

      // Insert based on priority
      this._insertByPriority(requestItem);
      
      // Start processing if not already running
      if (!this.processing) {
        this._processQueue();
      }
    });
  }

  /**
   * Insert request by priority (high -> normal -> low)
   */
  _insertByPriority(requestItem) {
    const priority = requestItem.options.priority;
    let insertIndex = this.queue.length;
    
    if (priority === 'high') {
      insertIndex = 0;
    } else if (priority === 'normal') {
      // Find the first low priority item or end of queue
      insertIndex = this.queue.findIndex(item => item.options.priority === 'low');
      if (insertIndex === -1) insertIndex = this.queue.length;
    }
    
    this.queue.splice(insertIndex, 0, requestItem);
  }

  /**
   * Process the request queue
   */
  async _processQueue() {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      // Check if we're currently rate limited
      if (this.rateLimitedUntil && Date.now() < this.rateLimitedUntil) {
        // Wait until rate limit expires, then continue
        await this._sleep(this.rateLimitedUntil - Date.now());
        this.rateLimitedUntil = null;
      }

      // Wait if we have too many concurrent requests
      while (this.concurrentRequests >= this.maxConcurrentRequests) {
        await this._sleep(50);
      }

      // Respect minimum delay between requests
      if (this.lastRequestTime) {
        const timeSinceLastRequest = Date.now() - this.lastRequestTime;
        if (timeSinceLastRequest < this.requestDelay) {
          await this._sleep(this.requestDelay - timeSinceLastRequest);
        }
      }

      const requestItem = this.queue.shift();
      this._executeRequest(requestItem);
    }

    this.processing = false;
  }

  /**
   * Execute a single request
   */
  async _executeRequest(requestItem) {
    this.concurrentRequests++;
    this.lastRequestTime = Date.now();

    try {
      // Add timeout wrapper
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), requestItem.options.timeout);
      });

      const response = await Promise.race([
        requestItem.requestFn(),
        timeoutPromise
      ]);

      // Track successful request
      this._trackRequest(requestItem, true);
      requestItem.resolve(response);

    } catch (error) {
      console.warn('RequestQueue: Request failed:', error.message || error);
      
      // Handle rate limiting
      if (error.status === 429 || error.response?.status === 429) {
        this._handleRateLimit(error);
        
        // Retry the request if retries are available
        if (requestItem.retryCount < requestItem.options.retries) {
          requestItem.retryCount++;
          
          // Re-add to front of queue for retry with exponential backoff
          await this._sleep(Math.min(1000 * Math.pow(2, requestItem.retryCount), 10000));
          this.queue.unshift(requestItem);
        } else {
          this._trackRequest(requestItem, false, '429');
          // Provide fallback error with more context
          const enhancedError = new Error('Rate limit exceeded. Please try again later.');
          enhancedError.originalError = error;
          enhancedError.isRateLimited = true;
          requestItem.reject(enhancedError);
        }
      } else if (error.status === 404 || error.response?.status === 404) {
        // Don't retry 404 errors
        this._trackRequest(requestItem, false, '404');
        const enhancedError = new Error('Resource not found');
        enhancedError.originalError = error;
        enhancedError.is404 = true;
        requestItem.reject(enhancedError);
      } else {
        // Handle other errors with exponential backoff
        if (requestItem.retryCount < requestItem.options.retries && this._isRetryableError(error)) {
          requestItem.retryCount++;
          
          // Add exponential backoff delay before retry
          const backoffDelay = Math.min(1000 * Math.pow(2, requestItem.retryCount), 5000);
          await this._sleep(backoffDelay);
          this.queue.unshift(requestItem);
        } else {
          this._trackRequest(requestItem, false, error.code || error.status);
          // Provide enhanced error information
          const enhancedError = new Error(this._getErrorMessage(error));
          enhancedError.originalError = error;
          enhancedError.isNetworkError = this._isNetworkError(error);
          requestItem.reject(enhancedError);
        }
      }
    } finally {
      this.concurrentRequests--;
    }
  }

  /**
   * Handle rate limiting detection
   */
  _handleRateLimit(error) {
    const retryAfter = this._parseRetryAfter(error) || (15 * 60 * 1000); // 15 min default
    this.rateLimitedUntil = Date.now() + retryAfter;
    
    console.warn(`RequestQueue: Rate limited detected, pausing requests until ${new Date(this.rateLimitedUntil)}`);
    
    // Reduce concurrent requests temporarily
    this.maxConcurrentRequests = Math.max(1, this.maxConcurrentRequests - 1);
    
    // Reset concurrent requests after rate limit period
    setTimeout(() => {
      this.maxConcurrentRequests = Math.min(6, this.maxConcurrentRequests + 1);
    }, retryAfter);
  }

  /**
   * Parse Retry-After header or error message
   */
  _parseRetryAfter(error) {
    try {
      // Try header first
      const retryAfter = error.response?.headers?.['retry-after'];
      if (retryAfter) {
        return parseInt(retryAfter) * 1000;
      }
      
      // Try error message
      const match = error.message?.match(/Expected available in (\d+) seconds/);
      if (match) {
        return parseInt(match[1]) * 1000;
      }
    } catch (parseError) {
      console.warn('Error parsing retry-after:', parseError);
    }
    
    return null;
  }

  /**
   * Check if an error is retryable
   */
  _isRetryableError(error) {
    const retryableCodes = ['ECONNRESET', 'ETIMEDOUT', 'ENOTFOUND', 'EAI_AGAIN'];
    const retryableStatus = [408, 429, 502, 503, 504];
    
    return retryableCodes.includes(error.code) || 
           retryableStatus.includes(error.status) ||
           retryableStatus.includes(error.response?.status);
  }

  /**
   * Track request for monitoring
   */
  _trackRequest(requestItem, success, errorCode = null) {
    const record = {
      timestamp: Date.now(),
      success,
      errorCode,
      priority: requestItem.options.priority,
      retryCount: requestItem.retryCount,
      processingTime: Date.now() - requestItem.timestamp
    };

    this.requestHistory.push(record);
    
    // Trim history to prevent memory leaks
    if (this.requestHistory.length > this.maxHistoryLength) {
      this.requestHistory.shift();
    }
  }

  /**
   * Get queue statistics
   */
  getStats() {
    const recent = this.requestHistory.slice(-20);
    const successRate = recent.length > 0 ? 
      (recent.filter(r => r.success).length / recent.length) * 100 : 0;

    return {
      queueLength: this.queue.length,
      concurrentRequests: this.concurrentRequests,
      rateLimitedUntil: this.rateLimitedUntil,
      successRate: Math.round(successRate),
      totalRequests: this.requestHistory.length,
      isRateLimited: this.rateLimitedUntil && Date.now() < this.rateLimitedUntil
    };
  }

  /**
   * Clear the queue and reset state
   */
  clear() {
    this.queue.forEach(item => {
      item.reject(new Error('Queue cleared'));
    });
    
    this.queue = [];
    this.processing = false;
    this.rateLimitedUntil = null;
    this.requestHistory = [];
  }

  /**
   * Utility method for sleeping
   */
  _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get user-friendly error message
   */
  _getErrorMessage(error) {
    if (error.message) return error.message;
    if (error.response?.data?.message) return error.response.data.message;
    if (error.response?.data?.error) return error.response.data.error;
    
    const statusMessages = {
      400: 'Invalid request',
      401: 'Authentication required',
      403: 'Access denied',
      404: 'Resource not found',
      500: 'Server error',
      502: 'Service temporarily unavailable',
      503: 'Service overloaded',
      504: 'Request timeout'
    };
    
    const status = error.status || error.response?.status;
    return statusMessages[status] || 'An error occurred processing your request';
  }

  /**
   * Check if error is network-related
   */
  _isNetworkError(error) {
    const networkCodes = ['ECONNRESET', 'ETIMEDOUT', 'ENOTFOUND', 'EAI_AGAIN', 'ECONNREFUSED'];
    return networkCodes.includes(error.code) || 
           error.message?.toLowerCase().includes('network') ||
           error.message?.toLowerCase().includes('fetch');
  }

  /**
   * Set custom configuration
   */
  configure(config) {
    if (config.maxConcurrentRequests !== undefined) {
      this.maxConcurrentRequests = config.maxConcurrentRequests;
    }
    if (config.requestDelay !== undefined) {
      this.requestDelay = config.requestDelay;
    }
    if (config.maxHistoryLength !== undefined) {
      this.maxHistoryLength = config.maxHistoryLength;
    }
  }
}

// Create singleton instance
const requestQueueManager = new RequestQueueManager();

// Development debugging
if (process.env.NODE_ENV === 'development') {
  window.requestQueueManager = requestQueueManager;
}

export default requestQueueManager;